import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useInView } from "react-intersection-observer";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  ChevronDown,
  MapPin,
  Clock,
  Star,
  BookOpen,
  Compass,
  SlidersHorizontal,
  UtensilsCrossed,
  Utensils,
  Pizza,
  Soup,
  Beer,
  Salad,
  Beef,
  Flame,
  Croissant,
  Leaf,
  Dices,
} from "lucide-react";
import SearchBar from "@/components/SearchBar";
import SEO from "@/components/SEO";
import { Cuisine } from "@/lib/types";
import { cn } from "@/lib/utils";
import hintergrundImage from "@/assets/hintergrund.webp";

/* ============================================================
   Reveal – dezentes Ein-/Aufblenden beim Scrollen (Intersection Observer)
   ============================================================ */
interface RevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

const Reveal = ({ children, className, delay = 0 }: RevealProps) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
    rootMargin: "0px 0px -40px 0px",
  });

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        "transition-all duration-700 ease-out motion-reduce:transition-none",
        inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

/* ============================================================
   SectionHeader – einheitliche Überschriften-Struktur
   ============================================================ */
interface SectionHeaderProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: string;
}

const SectionHeader = ({ eyebrow, title, subtitle }: SectionHeaderProps) => (
  <div className="mx-auto max-w-2xl text-center">
    <span className="inline-flex items-center rounded-full bg-primary/10 px-3.5 py-1.5 text-sm font-semibold uppercase tracking-wide text-primary">
      {eyebrow}
    </span>
    <h2 className="mt-5 font-display text-3xl font-bold leading-[1.15] tracking-tight text-neutral-darker sm:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-lg leading-relaxed text-gray-600">{subtitle}</p>
    )}
  </div>
);

/* ============================================================
   Küchen-Visuals: Icon + Farbverlauf je Küchenart (lucide-react)
   ============================================================ */
const CUISINE_VISUALS: Record<string, { Icon: LucideIcon; gradient: string }> = {
  Italienisch: { Icon: Pizza, gradient: "from-rose-500 to-orange-500" },
  Asiatisch: { Icon: Soup, gradient: "from-cyan-500 to-emerald-500" },
  Deutsch: { Icon: Beer, gradient: "from-amber-500 to-yellow-600" },
  Mediterran: { Icon: Salad, gradient: "from-emerald-500 to-teal-600" },
  Amerikanisch: { Icon: Beef, gradient: "from-red-500 to-amber-500" },
  Türkisch: { Icon: Flame, gradient: "from-orange-500 to-red-600" },
  Französisch: { Icon: Croissant, gradient: "from-violet-500 to-indigo-600" },
  Vegan: { Icon: Leaf, gradient: "from-lime-500 to-green-600" },
  "Überrasch mich": { Icon: Dices, gradient: "from-fuchsia-500 to-purple-600" },
};

const DEFAULT_VISUAL = { Icon: Utensils, gradient: "from-primary to-primary-dark" };

const getCuisineSubtitle = (count: number) => {
  if (count <= 0) return "Bald verfügbar";
  if (count === 1) return "1 Lokal";
  if (count < 50) return `${count} Lokale`;
  return `${count}+ Lokale`;
};

/* ============================================================
   CuisineCard – moderne, einheitliche Küchen-Kachel
   ============================================================ */
interface CuisineCardProps {
  cuisine: Cuisine;
  onClick: () => void;
}

const CuisineCard = ({ cuisine, onClick }: CuisineCardProps) => {
  const displayName = cuisine.name === "beliebig" ? "Überrasch mich" : cuisine.name;
  const visual = CUISINE_VISUALS[displayName] ?? DEFAULT_VISUAL;
  const { Icon } = visual;
  const hasImage = Boolean(cuisine.imageUrl && cuisine.imageUrl.trim() !== "");
  const count = cuisine.restaurantCount ?? 0;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${displayName} entdecken`}
      className="group relative flex aspect-[4/5] w-full flex-col justify-end overflow-hidden rounded-2xl bg-neutral-900 text-left shadow-sm ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {hasImage ? (
        <>
          <img
            src={cuisine.imageUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />
        </>
      ) : (
        <>
          <div className={cn("absolute inset-0 bg-gradient-to-br", visual.gradient)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <Icon
            className="absolute -right-3 -top-3 h-24 w-24 text-white/15"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </>
      )}

      <div className="relative z-10 p-4 sm:p-5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
          <Icon className="h-5 w-5 text-white" strokeWidth={2} aria-hidden="true" />
        </span>
        <h3 className="mt-3 text-lg font-bold leading-tight text-white drop-shadow-sm">
          {displayName}
        </h3>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-sm font-medium text-white/80">
            {getCuisineSubtitle(count)}
          </span>
          <span className="inline-flex h-7 w-7 translate-x-1 items-center justify-center rounded-full bg-white/20 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
            <ArrowRight className="h-3.5 w-3.5 text-white" />
          </span>
        </div>
      </div>
    </button>
  );
};

/* ============================================================
   Statische Inhalte
   ============================================================ */
const HERO_CHIPS: { Icon: LucideIcon; label: string }[] = [
  { Icon: BookOpen, label: "Echte Speisekarten" },
  { Icon: Clock, label: "Aktuelle Öffnungszeiten" },
  { Icon: Star, label: "Echte Bewertungen" },
];

const STEPS: { Icon: LucideIcon; title: string; text: string }[] = [
  {
    Icon: MapPin,
    title: "Standort wählen",
    text: "Gib deinen Ort ein oder nutze automatisch deine aktuelle Position.",
  },
  {
    Icon: SlidersHorizontal,
    title: "Passend filtern",
    text: "Grenze nach Küche, Preis und Bewertung ein – in wenigen Sekunden.",
  },
  {
    Icon: BookOpen,
    title: "Speisekarte ansehen",
    text: "Öffne echte Speisekarten und Öffnungszeiten direkt vom Lokal.",
  },
];

const FEATURES: { Icon: LucideIcon; title: string; text: string }[] = [
  {
    Icon: Compass,
    title: "Lokale Entdeckungen",
    text: "Finde die besten Restaurants in deiner Nähe – schnell, einfach und immer lokal.",
  },
  {
    Icon: UtensilsCrossed,
    title: "Echte Speisekarten",
    text: "Sieh vollständige Speisekarten direkt vom Restaurant – aktuell und ohne Umwege.",
  },
  {
    Icon: SlidersHorizontal,
    title: "Clevere Filter",
    text: "Filtere nach Küche, Preisspanne oder Bewertung und finde genau das Richtige.",
  },
];

/* ============================================================
   HomePage
   ============================================================ */
const HomePage = () => {
  const [, setLocation] = useLocation();

  const { data: cuisines = [], isLoading: isLoadingCuisines } = useQuery<Cuisine[]>({
    queryKey: ["/api/cuisines"],
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const defaultCuisines: Cuisine[] = [
    { id: 1, name: "Italienisch", order: 1, imageUrl: "", restaurantCount: 25 },
    { id: 2, name: "Asiatisch", order: 2, imageUrl: "", restaurantCount: 18 },
    { id: 3, name: "Deutsch", order: 3, imageUrl: "", restaurantCount: 15 },
    { id: 4, name: "Mediterran", order: 4, imageUrl: "", restaurantCount: 12 },
    { id: 5, name: "Amerikanisch", order: 5, imageUrl: "", restaurantCount: 10 },
    { id: 6, name: "Türkisch", order: 6, imageUrl: "", restaurantCount: 8 },
    { id: 7, name: "Französisch", order: 7, imageUrl: "", restaurantCount: 6 },
    { id: 8, name: "Vegan", order: 8, imageUrl: "", restaurantCount: 4 },
  ];

  const displayCuisines = cuisines.length >= 6 ? cuisines : defaultCuisines;

  const sortedCuisines = [...displayCuisines].sort((a, b) => {
    const aCount = a.restaurantCount || 0;
    const bCount = b.restaurantCount || 0;
    if (aCount > 0 && bCount === 0) return -1;
    if (aCount === 0 && bCount > 0) return 1;
    return 0;
  });

  const totalRestaurants = cuisines.reduce(
    (sum, c) => sum + (c.restaurantCount || 0),
    0,
  );

  const stats = [
    { value: totalRestaurants > 0 ? `${totalRestaurants}+` : "500+", label: "Restaurants" },
    { value: `${Math.max(displayCuisines.length, 8)}+`, label: "Küchenarten" },
    { value: "100%", label: "Echte Speisekarten" },
    { value: "0 €", label: "Kostenlos" },
  ];

  return (
    <div className="bg-white">
      <SEO
        title="Restaurants entdecken mit echten Speisekarten"
        description="Finde Restaurants in deiner Nähe und sieh echte Speisekarten direkt vom Restaurant."
      />

      {/* ===================== HERO ===================== */}
      <section className="relative isolate flex min-h-[calc(100svh-56px)] items-center overflow-hidden bg-black lg:min-h-[calc(100svh-64px)]">
        {/* Hintergrund + Overlays */}
        <div className="absolute inset-0 -z-10">
          <img
            src={hintergrundImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            className="h-full w-full scale-105 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/75 to-black/90" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_55%_at_50%_0%,rgba(0,149,68,0.25),transparent_70%)]" />
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/90 backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-light opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-light" />
                </span>
                Dein lokaler Speisekarten-Guide
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
                Restaurants entdecken mit{" "}
                <span className="bg-gradient-to-r from-primary-light via-primary-lighter to-primary-light bg-clip-text text-transparent">
                  echten Speisekarten
                </span>
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-white/80 sm:text-xl">
                Finde Lokale in deiner Nähe, sieh echte Speisekarten und aktuelle
                Öffnungszeiten – lokal, schnell und komplett kostenlos.
              </p>
            </Reveal>

            <Reveal delay={240} className="mt-10 w-full">
              <SearchBar />
            </Reveal>

            <Reveal delay={320} className="mt-8">
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                {HERO_CHIPS.map(({ Icon, label }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur-md"
                  >
                    <Icon className="h-4 w-4 text-primary-light" strokeWidth={2} />
                    {label}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={400} className="mt-12 w-full">
              <div className="mx-auto grid w-full max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-4">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    className="bg-white/[0.06] px-4 py-5 text-center backdrop-blur-sm"
                  >
                    <div className="font-display text-2xl font-bold text-white sm:text-3xl">
                      {s.value}
                    </div>
                    <div className="mt-1 text-xs font-medium text-white/60 sm:text-sm">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-6 hidden justify-center lg:flex">
          <ChevronDown className="h-6 w-6 animate-bounce text-white/40" />
        </div>
      </section>

      {/* ===================== SO FUNKTIONIERT'S ===================== */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeader
              eyebrow="So einfach geht's"
              title="In drei Schritten zum Lieblingslokal"
              subtitle="Kein Anmelden, kein Aufwand – finde in Sekunden das passende Restaurant."
            />
          </Reveal>

          <div className="relative mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
            <div className="pointer-events-none absolute inset-x-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent md:block" />
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 120}>
                <div className="relative flex flex-col items-center text-center">
                  <div className="relative z-10 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary/10 ring-8 ring-white">
                    <step.Icon className="h-8 w-8 text-primary" strokeWidth={1.75} />
                    <span className="absolute -right-1.5 -top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-bold text-white shadow-sm">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-neutral-darker">
                    {step.title}
                  </h3>
                  <p className="mt-2 max-w-xs leading-relaxed text-gray-600">
                    {step.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WARUM SPEISEGURU ===================== */}
      <section className="bg-neutral-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeader
              eyebrow="Warum SpeiseGuru?"
              title="Dein lokaler Restaurant-Guide"
              subtitle="Mit Herz gemacht – für alle, die gutes Essen und echte Lokale lieben."
            />
          </Reveal>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 120}>
                <div className="group h-full rounded-2xl border border-neutral-200/70 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/20 hover:shadow-xl">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                    <feature.Icon className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-6 text-xl font-bold text-neutral-darker">
                    {feature.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-gray-600">{feature.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BELIEBTE KÜCHENARTEN ===================== */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeader
              eyebrow="Beliebte Küchenarten"
              title="Worauf hast du Appetit?"
              subtitle="Entdecke Restaurants nach deiner Lieblingsküche – direkt in deiner Nähe."
            />
          </Reveal>

          <div className="mt-14">
            {isLoadingCuisines ? (
              <>
                <div className="hidden grid-cols-2 gap-5 sm:grid md:grid-cols-3 lg:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="aspect-[4/5] animate-pulse rounded-2xl bg-neutral-100"
                      style={{ animationDelay: `${i * 60}ms` }}
                    />
                  ))}
                </div>
                <div className="sm:hidden">
                  <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="aspect-[4/5] w-44 flex-shrink-0 animate-pulse rounded-2xl bg-neutral-100"
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Desktop / Tablet: Grid */}
                <div className="hidden grid-cols-2 gap-5 sm:grid md:grid-cols-3 lg:grid-cols-4">
                  {sortedCuisines.slice(0, 8).map((cuisine) => (
                    <Reveal key={cuisine.id}>
                      <CuisineCard
                        cuisine={cuisine}
                        onClick={() => setLocation(`/restaurants?cuisines=${cuisine.id}`)}
                      />
                    </Reveal>
                  ))}
                </div>

                {/* Mobile: horizontales Carousel */}
                <div className="sm:hidden">
                  <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 scrollbar-hide">
                    {sortedCuisines.slice(0, 8).map((cuisine) => (
                      <div key={cuisine.id} className="w-44 flex-shrink-0 snap-start">
                        <CuisineCard
                          cuisine={cuisine}
                          onClick={() => setLocation(`/restaurants?cuisines=${cuisine.id}`)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <Reveal className="mt-12 flex justify-center">
            <Link
              href="/restaurants"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-6 py-3 font-semibold text-neutral-darker shadow-sm transition-all duration-300 hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
            >
              Alle Restaurants durchsuchen
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="pb-24 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-dark via-primary to-primary-light px-6 py-16 text-center shadow-xl sm:px-16 sm:py-20">
              <div className="pointer-events-none absolute inset-0 opacity-40 [background:radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.35),transparent_45%),radial-gradient(circle_at_85%_80%,rgba(255,255,255,0.22),transparent_42%)]" />
              <div className="relative">
                <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-white sm:text-4xl">
                  Bereit für dein nächstes Lieblingslokal?
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-lg text-white/90">
                  Entdecke Restaurants mit echten Speisekarten in deiner Nähe – jetzt,
                  kostenlos und ohne Anmeldung.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Link
                    href="/restaurants"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-primary shadow-lg transition-transform duration-300 hover:scale-[1.03] sm:w-auto"
                  >
                    Restaurants entdecken
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    href="/wie-funktioniert"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/20 sm:w-auto"
                  >
                    So funktioniert's
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
